import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getRandomLoadingMessage } from '../../utils/loadingMessages';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';

// ISO alpha-2 → numeric lookup for choropleth matching
const A2_TO_NUM = {
  AF:4,AL:8,DZ:12,AO:24,AR:32,AU:36,AT:40,AZ:31,BS:44,BH:48,BD:50,BY:112,BE:56,BZ:84,BJ:204,BT:64,
  BO:68,BA:70,BW:72,BR:76,BN:96,BG:100,BF:854,BI:108,KH:116,CM:120,CA:124,CF:140,TD:148,CL:152,
  CN:156,CO:170,CG:178,CD:180,CR:188,HR:191,CU:192,CY:196,CZ:203,DK:208,DO:214,EC:218,EG:818,
  SV:222,ET:231,FI:246,FR:250,GA:266,GH:288,GR:300,GT:320,GN:324,HT:332,HN:340,HK:344,HU:348,
  IN:356,ID:360,IR:364,IQ:368,IE:372,IL:376,IT:380,CI:384,JM:388,JP:392,JO:400,KZ:398,KE:404,
  KW:414,LB:422,LY:434,MG:450,MW:454,MY:458,ML:466,MR:478,MX:484,MA:504,MZ:508,MM:104,NA:516,
  NP:524,NL:528,NZ:554,NI:558,NE:562,NG:566,NO:578,PK:586,PA:591,PY:600,PE:604,PH:608,PL:616,
  PT:620,RO:642,RU:643,RW:646,SA:682,SN:686,RS:688,SL:694,SO:706,ZA:710,SS:728,ES:724,LK:144,
  SD:729,SE:752,CH:756,SY:760,TW:158,TZ:834,TH:764,TN:788,TR:792,UG:800,UA:804,AE:784,GB:826,
  US:840,UY:858,VE:862,VN:704,YE:887,ZM:894,ZW:716,KR:410,SK:703,SG:702,MK:807,LT:440,LV:428,
  EE:233,ME:499,BO:68,MU:480,IS:352,LU:442,MT:470,AL:8,BH:48,CY:196,MN:496,KG:417,TJ:762,
  TM:795,UZ:860,AM:51,GE:268,MD:498,BY:112,MK:807,XK:0,PR:0,
};

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

function VisitorMap({ byCountry }) {
  const [tooltip, setTooltip] = useState(null);

  // Build lookup: numeric_id → { country, count }
  const countMap = {};
  let maxCount = 0;
  (byCountry || []).forEach(({ country, country_code, count }) => {
    const num = A2_TO_NUM[country_code];
    if (num) {
      countMap[num] = { country, count };
      if (count > maxCount) maxCount = count;
    }
  });

  function getColor(geoId) {
    const entry = countMap[parseInt(geoId)];
    if (!entry) return '#2d1f5e';
    const intensity = Math.max(0.15, entry.count / maxCount);
    // Purple scale: light at low counts, vivid at high
    const r = Math.round(102 + (1 - intensity) * 60);
    const g = Math.round(20 + (1 - intensity) * 30);
    const b = Math.round(200 + (1 - intensity) * 55);
    return `rgb(${r},${g},${b})`;
  }

  return (
    <div style={{ position: 'relative', background: '#1a0f3c', borderRadius: 8, padding: '8px 0', overflow: 'hidden' }}>
      <ComposableMap
        projectionConfig={{ scale: 147 }}
        style={{ width: '100%', height: 'auto' }}
      >
        <ZoomableGroup center={[0, 10]} zoom={1}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map(geo => {
                const entry = countMap[parseInt(geo.id)];
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getColor(geo.id)}
                    stroke="#0d0829"
                    strokeWidth={0.4}
                    style={{
                      default: { outline: 'none' },
                      hover:   { fill: '#a855f7', outline: 'none', cursor: entry ? 'pointer' : 'default' },
                      pressed: { outline: 'none' },
                    }}
                    onMouseEnter={(e) => {
                      if (entry) {
                        setTooltip({ x: e.clientX, y: e.clientY, country: entry.country, count: entry.count });
                      }
                    }}
                    onMouseMove={(e) => {
                      if (entry) setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 36,
          background: '#2d1f5e', border: '1px solid #7c3aed', borderRadius: 6,
          padding: '6px 12px', fontSize: 13, color: '#fff', pointerEvents: 'none',
          zIndex: 9999, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}>
          <strong>{tooltip.country}</strong> — {tooltip.count.toLocaleString()} visit{tooltip.count !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

const API = '/api/analytics/stats/';

function fmt(n) {
  if (n == null) return '—';
  return n.toLocaleString();
}

function pct(part, total) {
  if (!total) return '0%';
  return ((part / total) * 100).toFixed(1) + '%';
}

function barWidth(count, max) {
  if (!max) return '0%';
  return Math.max(2, (count / max) * 100) + '%';
}

function shortPath(path) {
  return path.length > 40 ? path.slice(0, 40) + '…' : path;
}

function shortRef(ref) {
  try {
    const u = new URL(ref);
    return u.hostname + (u.pathname !== '/' ? u.pathname.slice(0, 20) : '');
  } catch {
    return ref.slice(0, 40);
  }
}

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return Math.round(diff) + 's ago';
  if (diff < 3600) return Math.round(diff / 60) + 'm ago';
  if (diff < 86400) return Math.round(diff / 3600) + 'h ago';
  return Math.round(diff / 86400) + 'd ago';
}

const DEVICE_EMOJI = { mobile: '📱', tablet: '📟', desktop: '🖥️', unknown: '❓' };

// ─── sub-components ──────────────────────────────────────────────────────────

function StatCard({ label, value, sub }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardValue}>{fmt(value)}</div>
      <div style={styles.cardLabel}>{label}</div>
      {sub && <div style={styles.cardSub}>{sub}</div>}
    </div>
  );
}

function BarList({ items, labelKey, countKey, total, color = '#a855f7' }) {
  if (!items || items.length === 0) return <div style={styles.empty}>No data yet</div>;
  const max = items[0][countKey];
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={styles.barRow}>
          <div style={styles.barLabel}>{item[labelKey] || '(direct)'}</div>
          <div style={styles.barTrack}>
            <div style={{ ...styles.barFill, width: barWidth(item[countKey], max), background: color }} />
          </div>
          <div style={styles.barCount}>
            {fmt(item[countKey])}
            {total ? <span style={styles.barPct}> {pct(item[countKey], total)}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function DayChart({ data }) {
  const [hovered, setHovered] = useState(null);
  if (!data || data.length === 0) return <div style={styles.empty}>No data yet</div>;

  const W = 800, H = 180;
  const PAD = { top: 16, right: 20, bottom: 32, left: 44 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const max = Math.max(...data.map(d => d.count), 1);
  const n = data.length;

  const xOf = i => PAD.left + (n === 1 ? chartW / 2 : (i / (n - 1)) * chartW);
  const yOf = v => PAD.top + chartH - (v / max) * chartH;

  // Build smooth bezier path
  const pts = data.map((d, i) => [xOf(i), yOf(d.count)]);
  const linePath = pts.reduce((acc, [x, y], i) => {
    if (i === 0) return `M ${x} ${y}`;
    const [px, py] = pts[i - 1];
    const cpx = (px + x) / 2;
    return `${acc} C ${cpx} ${py}, ${cpx} ${y}, ${x} ${y}`;
  }, '');
  const areaPath = `${linePath} L ${pts[pts.length - 1][0]} ${PAD.top + chartH} L ${pts[0][0]} ${PAD.top + chartH} Z`;

  // Y-axis gridlines (4 lines)
  const yTicks = [0.25, 0.5, 0.75, 1].map(f => ({
    y: yOf(f * max),
    label: Math.round(f * max),
  }));

  // X-axis labels — show ~6 evenly spaced
  const xStep = Math.max(1, Math.floor(n / 6));
  const xTicks = data.reduce((acc, d, i) => {
    if (i % xStep === 0 || i === n - 1) acc.push({ x: xOf(i), label: d.date ? d.date.slice(5) : '' });
    return acc;
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHovered(null)}
      >
        <defs>
          <linearGradient id="lineAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Gridlines */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={t.y} x2={W - PAD.right} y2={t.y}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <text x={PAD.left - 6} y={t.y + 4} textAnchor="end"
              fill="#6b7280" fontSize="11">{t.label}</text>
          </g>
        ))}

        {/* X-axis labels */}
        {xTicks.map((t, i) => (
          <text key={i} x={t.x} y={H - 6} textAnchor="middle"
            fill="#6b7280" fontSize="11">{t.label}</text>
        ))}

        {/* Area fill */}
        <path d={areaPath} fill="url(#lineAreaGrad)" />

        {/* Line */}
        <path d={linePath} fill="none" stroke="#a855f7" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" />

        {/* Hover hit areas + dots */}
        {pts.map(([x, y], i) => (
          <g key={i}>
            <rect
              x={x - (chartW / n / 2)} y={PAD.top}
              width={chartW / n} height={chartH}
              fill="transparent"
              onMouseEnter={() => setHovered(i)}
            />
            {hovered === i && (
              <>
                <line x1={x} y1={PAD.top} x2={x} y2={PAD.top + chartH}
                  stroke="rgba(168,85,247,0.4)" strokeWidth="1" strokeDasharray="3,3" />
                <circle cx={x} cy={y} r="5" fill="#a855f7"
                  stroke="#1a0f3c" strokeWidth="2" />
              </>
            )}
          </g>
        ))}
      </svg>

      {/* Tooltip */}
      {hovered !== null && (
        <div style={{
          position: 'absolute',
          left: `${(xOf(hovered) / W) * 100}%`,
          top: `${(yOf(data[hovered].count) / H) * 100}%`,
          transform: 'translate(-50%, -140%)',
          background: '#2d1f5e',
          border: '1px solid #7c3aed',
          borderRadius: 6,
          padding: '5px 11px',
          fontSize: 13,
          color: '#fff',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}>
          <strong>{data[hovered].date}</strong>
          <br />
          {data[hovered].count.toLocaleString()} visit{data[hovered].count !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function AdminAnalytics() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentPage, setRecentPage] = useState(1);

  const fetchStats = useCallback(async (page = recentPage) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      const res = await fetch(`${API}?days=${days}&recent_page=${page}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (res.status === 403 || res.status === 401) {
        setError('admin_only');
        return;
      }
      if (!res.ok) throw new Error('Server error');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days, recentPage]);

  // Reset to page 1 when period changes
  const handleSetDays = (d) => {
    setRecentPage(1);
    setDays(d);
  };

  const handleRecentPage = (newPage) => {
    setRecentPage(newPage);
    fetchStats(newPage);
    // Scroll recent visitors table into view
    document.getElementById('recent-visitors-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!isAuthenticated) { navigate('/signin'); return; }
    if (!user?.is_staff) { navigate('/404'); return; }
    fetchStats();
  }, [isAuthenticated, user, fetchStats, navigate]);

  if (!isAuthenticated) return null;

  if (loading) return (
    <div style={styles.page}>
      <div style={styles.loading}>{getRandomLoadingMessage()}</div>
    </div>
  );

  if (error === 'admin_only') return <Navigate to="/404" replace />;

  if (error) return (
    <div style={styles.page}>
      <div style={styles.errorBox}>Error: {error}</div>
    </div>
  );

  const { overview, visits_by_day, top_pages, by_browser, by_os, by_device, by_country, top_referrers, recent_visitors, recent_total, recent_per_page } = data || {};
  const totalRecentPages = recent_per_page && recent_total ? Math.ceil(recent_total / recent_per_page) : 1;
  const totalVisits = overview?.total_visits || 0;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 Visitor Analytics</h1>
          <p style={styles.subtitle}>unsupervisedbias.com</p>
        </div>
        <div style={styles.periodBtns}>
          {[7, 14, 30, 90].map(d => (
            <button
              key={d}
              style={{ ...styles.periodBtn, ...(days === d ? styles.periodBtnActive : {}) }}
              onClick={() => handleSetDays(d)}
            >
              {d}d
            </button>
          ))}
          <button style={styles.refreshBtn} onClick={fetchStats}>↻</button>
        </div>
      </div>

      {/* Overview cards */}
      <div style={styles.cards}>
        <StatCard label="Total Visits" value={overview?.total_visits} sub={`last ${days} days`} />
        <StatCard label="Unique Visitors" value={overview?.unique_visitors} sub="by IP address" />
        <StatCard label="Pages Visited" value={overview?.unique_pages} sub="distinct paths" />
      </div>

      {/* Visits over time */}
      <div style={{...styles.section, maxWidth: 1200, margin: '0 auto 16px'}}>
        <h2 style={styles.sectionTitle}>Visits Over Time</h2>
        <DayChart data={visits_by_day} />
      </div>

      {/* Visitor Map */}
      <div style={{...styles.section, maxWidth: 1200, margin: '0 auto 16px'}}>
        <h2 style={styles.sectionTitle}>Visitor Map</h2>
        <VisitorMap byCountry={by_country} />
      </div>

      {/* Two-column row: Device + Browser */}
      <div style={styles.twoCol}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Device Type</h2>
          <div>
            {(by_device || []).map((d, i) => (
              <div key={i} style={styles.deviceRow}>
                <span>{DEVICE_EMOJI[d.device_type] || '❓'} {d.device_type}</span>
                <span style={styles.deviceCount}>{fmt(d.count)} <span style={styles.barPct}>({pct(d.count, totalVisits)})</span></span>
              </div>
            ))}
            {(!by_device || by_device.length === 0) && <div style={styles.empty}>No data</div>}
          </div>
        </div>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Browsers</h2>
          <BarList items={by_browser} labelKey="browser" countKey="count" total={totalVisits} color="#7c3aed" />
        </div>
      </div>

      {/* Two-column row: OS + Country */}
      <div style={styles.twoCol}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Operating Systems</h2>
          <BarList items={by_os} labelKey="os" countKey="count" total={totalVisits} color="#6d28d9" />
        </div>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Top Countries</h2>
          <BarList items={by_country} labelKey="country" countKey="count" total={totalVisits} color="#5b21b6" />
        </div>
      </div>

      {/* Top pages */}
      <div style={{...styles.section, maxWidth: 1200, margin: '0 auto 16px'}}>
        <h2 style={styles.sectionTitle}>Top Pages</h2>
        <BarList items={top_pages} labelKey="path" countKey="count" total={totalVisits} color="#a855f7" />
      </div>

      {/* Top referrers */}
      <div style={{...styles.section, maxWidth: 1200, margin: '0 auto 16px'}}>
        <h2 style={styles.sectionTitle}>Top Referrers</h2>
        {top_referrers && top_referrers.length > 0 ? (
          <BarList
            items={top_referrers.map(r => ({ ...r, ref_short: shortRef(r.referrer) }))}
            labelKey="ref_short"
            countKey="count"
            total={totalVisits}
            color="#8b5cf6"
          />
        ) : (
          <div style={styles.empty}>No referrer data yet (direct traffic only)</div>
        )}
      </div>

      {/* Recent visitors */}
      <div id="recent-visitors-section" style={{...styles.section, maxWidth: 1200, margin: '0 auto 16px'}}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <h2 style={{ ...styles.sectionTitle, margin: 0 }}>
            Recent Visitors
            {recent_total != null && (
              <span style={{ fontSize: 13, fontWeight: 400, color: '#a78bfa', marginLeft: 10 }}>
                {recent_total.toLocaleString()} total · page {recentPage} of {totalRecentPages}
              </span>
            )}
          </h2>
          {totalRecentPages > 1 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                style={{ ...styles.periodBtn, ...(recentPage === 1 ? { opacity: 0.4, cursor: 'default' } : {}) }}
                onClick={() => recentPage > 1 && handleRecentPage(recentPage - 1)}
                disabled={recentPage === 1}
              >← Prev</button>
              <span style={{ color: '#a78bfa', fontSize: 13 }}>
                {recentPage} / {totalRecentPages}
              </span>
              <button
                style={{ ...styles.periodBtn, ...(recentPage >= totalRecentPages ? { opacity: 0.4, cursor: 'default' } : {}) }}
                onClick={() => recentPage < totalRecentPages && handleRecentPage(recentPage + 1)}
                disabled={recentPage >= totalRecentPages}
              >Next →</button>
            </div>
          )}
        </div>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Time', 'IP', 'Page', 'Browser', 'OS', 'Device', 'Location', 'Network', 'Language'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(recent_visitors || []).map((v, i) => (
                <tr key={i} style={i % 2 === 0 ? styles.trEven : {}}>
                  <td style={styles.td} title={v.timestamp}>{timeAgo(v.timestamp)}</td>
                  <td style={styles.td}>{v.ip_address || '—'}</td>
                  <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.path}>{shortPath(v.path)}</td>
                  <td style={styles.td}>{v.browser || '—'}</td>
                  <td style={styles.td}>{v.os || '—'}</td>
                  <td style={styles.td}>{DEVICE_EMOJI[v.device_type] || ''} {v.device_type}</td>
                  <td style={styles.td}>{v.city && v.country ? `${v.city}, ${v.country}` : v.country || '—'}</td>
                  <td style={{ ...styles.td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={v.carrier || ''}>
                    {v.carrier
                      ? <span>{v.is_mobile_ip ? '📶 ' : ''}{v.carrier}</span>
                      : '—'}
                  </td>
                  <td style={styles.td}>{v.language || '—'}</td>
                </tr>
              ))}
              {(!recent_visitors || recent_visitors.length === 0) && (
                <tr><td colSpan={9} style={{ ...styles.td, textAlign: 'center', color: '#888' }}>No visits recorded yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalRecentPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
            <button
              style={{ ...styles.periodBtn, ...(recentPage === 1 ? { opacity: 0.4, cursor: 'default' } : {}) }}
              onClick={() => recentPage > 1 && handleRecentPage(recentPage - 1)}
              disabled={recentPage === 1}
            >← Prev</button>
            <span style={{ color: '#a78bfa', fontSize: 13, lineHeight: '30px' }}>
              Page {recentPage} of {totalRecentPages}
            </span>
            <button
              style={{ ...styles.periodBtn, ...(recentPage >= totalRecentPages ? { opacity: 0.4, cursor: 'default' } : {}) }}
              onClick={() => recentPage < totalRecentPages && handleRecentPage(recentPage + 1)}
              disabled={recentPage >= totalRecentPages}
            >Next →</button>
          </div>
        )}
        <p style={styles.note}>
          📍 Location data is resolved asynchronously from IP addresses (may take a moment to appear for new visits).
          <br />
          🔒 Demographics like age/gender are not available — browsers don't expose this information.
        </p>
      </div>
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1a',
    color: '#e5e7eb',
    padding: '100px 24px 60px',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  loading: {
    textAlign: 'center',
    marginTop: 80,
    color: '#a855f7',
    fontSize: 18,
  },
  errorBox: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 12,
    padding: '24px 32px',
    maxWidth: 500,
    margin: '80px auto',
    textAlign: 'center',
    color: '#fca5a5',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 32,
    maxWidth: 1200,
    margin: '0 auto 32px',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#fff',
    margin: 0,
  },
  subtitle: {
    color: '#9ca3af',
    margin: '4px 0 0',
    fontSize: 14,
  },
  periodBtns: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  periodBtn: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: '#d1d5db',
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 0.2s',
  },
  periodBtnActive: {
    background: 'rgba(168,85,247,0.25)',
    border: '1px solid rgba(168,85,247,0.5)',
    color: '#d8b4fe',
  },
  refreshBtn: {
    background: 'none',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: '#9ca3af',
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 16,
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    maxWidth: 1200,
    margin: '0 auto 28px',
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '20px 24px',
    textAlign: 'center',
  },
  cardValue: {
    fontSize: 36,
    fontWeight: 700,
    color: '#a855f7',
    lineHeight: 1,
  },
  cardLabel: {
    fontSize: 14,
    color: '#e5e7eb',
    marginTop: 6,
    fontWeight: 500,
  },
  cardSub: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  section: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 14,
    padding: '20px 24px',
    flex: 1,
    minWidth: 0,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#d1d5db',
    marginTop: 0,
    marginBottom: 16,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    fontSize: 12,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16,
    maxWidth: 1200,
    margin: '0 auto 16px',
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  barLabel: {
    minWidth: 100,
    maxWidth: 160,
    fontSize: 13,
    color: '#d1d5db',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  barTrack: {
    flex: 1,
    background: 'rgba(255,255,255,0.07)',
    borderRadius: 4,
    height: 8,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.6s ease',
  },
  barCount: {
    fontSize: 13,
    color: '#e5e7eb',
    minWidth: 60,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  barPct: {
    color: '#6b7280',
    fontSize: 11,
  },
  // dayChart styles removed — replaced by SVG line chart

  deviceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    fontSize: 14,
    color: '#d1d5db',
    textTransform: 'capitalize',
  },
  deviceCount: {
    fontVariantNumeric: 'tabular-nums',
    color: '#e5e7eb',
  },
  empty: {
    color: '#6b7280',
    fontSize: 13,
    fontStyle: 'italic',
    padding: '8px 0',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.06)',
    color: '#9ca3af',
    fontWeight: 600,
    textAlign: 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    whiteSpace: 'nowrap',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  td: {
    padding: '9px 12px',
    color: '#d1d5db',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    verticalAlign: 'middle',
  },
  trEven: {
    background: 'rgba(255,255,255,0.015)',
  },
  note: {
    marginTop: 14,
    fontSize: 12,
    color: '#6b7280',
    lineHeight: 1.7,
  },
};

// end AdminAnalytics
